import { useEffect, useState } from "react";

import { Alert, Button, Tab, TabList } from "@realroof/ui";

import { api, canModerate, REASON_LABEL, STATUS_LABEL, type ReportItem } from "../api";
import { when } from "../format";
import { Link } from "../router";
import { useSession } from "../session";
import { Table, td, th } from "../table";

type Filter = "open" | "dismissed" | "actioned";
const TABS: [Filter, string][] = [
  ["open", "Open"],
  ["actioned", "Ad taken down"],
  ["dismissed", "Dismissed"],
];

/** Visitors' reports, oldest open first. Take an ad down from its page; dismiss a report here. */
export function Reports() {
  const { me } = useSession();
  const [filter, setFilter] = useState<Filter>("open");
  const [rows, setRows] = useState<ReportItem[] | null>(null);
  const [message, setMessage] = useState<{ kind: "error" | "success"; text: string } | null>(null);

  const load = (f: Filter) =>
    api<ReportItem[]>(`/admin/reports?status=${f}`)
      .then(setRows)
      .catch((err: Error) => setMessage({ kind: "error", text: err.message }));
  useEffect(() => {
    setRows(null);
    load(filter);
  }, [filter]);

  async function dismiss(id: string) {
    setMessage(null);
    try {
      await api(`/admin/reports/${id}/dismiss`, { method: "POST" });
      setMessage({ kind: "success", text: "Report dismissed; the ad stays live." });
      await load(filter);
    } catch (err) {
      setMessage({ kind: "error", text: (err as Error).message });
    }
  }

  return (
    <section className="flex flex-col gap-4">
      <h1 className="text-2xl font-bold">Reports from visitors</h1>
      <TabList>
        {TABS.map(([f, label]) => (
          <Tab key={f} selected={f === filter} className="flex-none" onClick={() => setFilter(f)}>
            {label}
          </Tab>
        ))}
      </TabList>
      {message && <Alert tone={message.kind}>{message.text}</Alert>}
      {rows === null && <p className="text-muted-foreground">Loading…</p>}
      {rows?.length === 0 && <p className="text-muted-foreground">{filter === "open" ? "No open reports." : "Nothing here."}</p>}
      {rows && rows.length > 0 && (
        <Table>
          <thead>
            <tr>
              <th className={th}>Ad</th>
              <th className={th}>Reason</th>
              <th className={th}>Reported</th>
              <th className={th} />
            </tr>
          </thead>
          <tbody>
            {rows.map((r) => (
              <tr key={r.id}>
                <td className={td}>
                  <Link to={`/ads/${r.listing_id}`} className="font-semibold">
                    {r.title}
                  </Link>
                  <div className="font-mono text-xs text-muted-foreground">
                    {r.public_id} · {STATUS_LABEL[r.listing_status]}
                  </div>
                </td>
                <td className={td}>
                  {REASON_LABEL[r.reason] ?? r.reason}
                  {r.details && <div className="text-sm text-muted-foreground">“{r.details}”</div>}
                </td>
                <td className={`${td} whitespace-nowrap`}>
                  {when(r.created_at)}
                  <div className="text-xs text-muted-foreground">{r.reporter}</div>
                </td>
                <td className={td}>
                  {filter === "open" && canModerate(me?.staff_role) && (
                    <Button size="sm" onClick={() => dismiss(r.id)}>
                      Dismiss
                    </Button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </Table>
      )}
    </section>
  );
}
